import type {
  LotInput,
  LotTakeInput,
  MovementLotView,
  StockCountStatus,
  StockMovementView,
} from "@cx/contracts";
import {
  LOT_ALERT_DEFAULT_DAYS,
  MAX_LOT_CODE_LENGTH,
  formatDateBR,
  formatQty,
  toQ4,
  type LotStatus,
  type StatusTone,
} from "@cx/domain";

const ORIGIN: Record<string, string> = {
  sales_order: "Pedido de venda",
  invoice: "Nota fiscal",
  purchase_order: "Pedido de compra",
  production_order: "Ordem de produção",
  stock_count: "Conferência de estoque",
  pos_sale: "PDV",
};

/** Descrição do movimento no extrato (RF-SUP-01). */
export function movementLabel(
  m: Pick<StockMovementView, "kind" | "deltaQ4" | "originType" | "transferGroupId">,
) {
  const dir = m.deltaQ4 >= 0 ? "entrada" : "saída";
  if (m.originType === "reversal") return `Estorno (${dir})`;
  if (m.transferGroupId) return `Transferência (${dir})`;
  if (m.kind === "B") return "Balanço";
  const base = m.kind === "E" ? "Entrada" : "Saída";
  const origin = m.originType ? ORIGIN[m.originType] : undefined;
  return origin ? `${base} · ${origin}` : base;
}

/** Quantidade com sinal: "+10", "-2,5", "0". */
export function signedQty(q4: number): string {
  return q4 > 0 ? `+${formatQty(q4)}` : formatQty(q4);
}

/** Chave nova por tentativa de envio (o modal gera ao abrir e reaproveita em reenvio do mesmo formulário). */
export const newIdempotencyKey = () => crypto.randomUUID();

// ===== Lotes (T3.2, RF-SUP-07) =====

export const LOT_STATUS_LABELS: Readonly<Record<LotStatus, string>> = {
  expired: "Vencido",
  expiring: "Vencendo",
  valid: "Válido",
  no_expiry: "Sem validade",
};

export const LOT_STATUS_TONES: Readonly<Record<LotStatus, StatusTone>> = {
  expired: "danger",
  expiring: "warning",
  valid: "success",
  no_expiry: "secondary",
};

/** Lote digitado numa entrada (datas como no `<input type="date">`, vazio = sem data). */
export type LotDraft = { code: string; manufacturedAt: string; expiresAt: string };
export const emptyLotDraft = (): LotDraft => ({ code: "", manufacturedAt: "", expiresAt: "" });

/** Corpo do lote para a API; código vazio = sem lote. */
export function lotPayload(d: LotDraft): LotInput | null {
  const code = d.code.trim();
  if (!code) return null;
  return { code, manufacturedAt: d.manufacturedAt || null, expiresAt: d.expiresAt || null };
}

/** Problema do lote na tela (o servidor confere de novo). */
export function lotDraftProblem(d: LotDraft, required: boolean): string | null {
  const code = d.code.trim();
  if (!code) return required ? "Informe o lote (o produto controla lotes)." : null;
  if (code.length > MAX_LOT_CODE_LENGTH) return `O lote tem até ${MAX_LOT_CODE_LENGTH} caracteres.`;
  if (d.manufacturedAt && d.expiresAt && d.expiresAt < d.manufacturedAt)
    return "A validade não pode ser anterior à fabricação.";
  return null;
}

/** Escolha manual dos lotes numa saída: só os lotes com quantidade; nenhum = automático (FEFO). */
export function lotTakesPayload(qty: Readonly<Record<string, number | null>>): LotTakeInput[] | null {
  const takes = Object.entries(qty)
    .filter(([, q]) => (q ?? 0) > 0)
    .map(([lotId, q]) => ({ lotId, qtyQ4: q! }));
  return takes.length ? takes : null;
}

/** A escolha manual cobre a saída inteira: a soma dos lotes é a quantidade. */
export function lotTakesProblem(
  takes: readonly LotTakeInput[] | null,
  totalQ4: number | null,
): string | null {
  if (!takes) return null;
  const sum = takes.reduce((a, t) => a + t.qtyQ4, 0);
  if (sum === (totalQ4 ?? 0)) return null;
  return `A soma dos lotes (${formatQty(sum)}) deve ser igual à quantidade (${formatQty(totalQ4 ?? 0)}).`;
}

/** "L1 (-2) · L2 (+1,5)" no extrato. */
export const movementLotsText = (lots: readonly MovementLotView[]): string =>
  lots.map((l) => `${l.code} (${signedQty(l.deltaQ4)})`).join(" · ");

/** Validade com o prazo quando vencido ou perto de vencer. */
export function lotValidity(expiresAt: string | null, daysToExpire: number | null): string {
  if (!expiresAt) return "Sem validade";
  const date = formatDateBR(expiresAt);
  const d = daysToExpire;
  if (d === null || d > LOT_ALERT_DEFAULT_DAYS) return date;
  if (d < 0) return `${date} (vencido há ${-d} ${d === -1 ? "dia" : "dias"})`;
  if (d === 0) return `${date} (vence hoje)`;
  if (d === 1) return `${date} (vence amanhã)`;
  return `${date} (vence em ${d} dias)`;
}

// ===== Conferência de estoque por leitura (T3.2, RF-SUP-06) =====

export const COUNT_STATUS_LABELS: Readonly<Record<StockCountStatus, string>> = {
  open: "Em conferência",
  applied: "Aplicada",
  canceled: "Cancelada",
};

export const COUNT_STATUS_TONES: Readonly<Record<StockCountStatus, StatusTone>> = {
  open: "info",
  applied: "success",
  canceled: "secondary",
};

/**
 * Leitura do campo do leitor: "12*7891234567895" lê 12 de uma vez; sem multiplicador vale a quantidade do campo
 * (padrão 1). Multiplicador inválido ou zero = leitura recusada (null); texto que não é número fica no código.
 */
export function scanEntry(text: string, qtyQ4: number | null): { code: string; qtyQ4: number } | null {
  const raw = text.trim();
  if (!raw) return null;
  const m = /^([\d.,]+)\*(.+)$/.exec(raw);
  if (m) {
    let q: number;
    try {
      q = toQ4(m[1]!);
    } catch {
      return null;
    }
    const code = m[2]!.trim();
    return q > 0 && code ? { code, qtyQ4: q } : null;
  }
  return { code: raw, qtyQ4: qtyQ4 && qtyQ4 > 0 ? qtyQ4 : 10_000 };
}
